import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SectionHeading } from "@/components/site/Primitives";
import { Counter } from "@/components/site/Counter";

const DEMAND = [
  { segment: "Relationship Mgmt", Monaco: 86, Geneva: 92 },
  { segment: "Family Office", Monaco: 78, Geneva: 54 },
  { segment: "Hedge Funds", Monaco: 38, Geneva: 74 },
  { segment: "Compliance", Monaco: 64, Geneva: 69 },
  { segment: "Wealth Planning", Monaco: 52, Geneva: 48 },
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="rounded-[10px] border border-[#E2DCCF] bg-white p-3 text-[12px] shadow-lg">
      <p className="font-semibold text-[color:var(--bp-text-on-light)]">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>{p.dataKey}: {p.value}</p>
      ))}
    </div>
  );
};

export const MarketIntelligence = ({ counters = [] }) => {
  return (
    <section className="section-py bg-[color:var(--bp-cream)]" data-testid="market-intelligence-section">
      <div className="container-bp grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="rounded-[var(--radius-lg)] border border-[#E6E0D4] bg-white p-6 shadow-[0_20px_50px_rgba(10,31,61,0.10)]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--bp-text-body-on-light)]">
              Hiring demand index · Monaco vs Geneva
            </p>
            <div className="mt-4 h-[300px] w-full" data-testid="market-intelligence-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={DEMAND} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E6E0D4" />
                  <XAxis dataKey="segment" tick={{ fontSize: 10, fill: "#6B6459" }} interval={0} angle={-18} textAnchor="end" height={56} />
                  <YAxis tick={{ fontSize: 11, fill: "#6B6459" }} />
                  <Tooltip cursor={{ fill: "rgba(45,181,165,0.06)" }} content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Monaco" fill="#D4AF37" radius={[4, 4, 0, 0]} animationDuration={1400} />
                  <Bar dataKey="Geneva" fill="#2DB5A5" radius={[4, 4, 0, 0]} animationDuration={1600} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Reveal>

        <div>
          <SectionHeading
            tone="light"
            eyebrow="Proprietary Intelligence"
            title="Monaco-Geneva intelligence no other firm can match."
            intro="No competitor has genuine, on-the-ground coverage of both markets. We translate that dual-market view into compensation, mobility and demand insight you can act on."
          />

          <div className="mt-9 grid grid-cols-2 gap-x-8 gap-y-7">
            {counters.map((c) => (
              <Reveal key={c.id}>
                <div data-testid="market-intelligence-counter">
                  <p className="font-display text-3xl font-semibold text-[color:var(--bp-text-on-light)] sm:text-4xl">
                    <Counter value={c.value} prefix={c.prefix || ""} suffix={c.suffix || ""} decimals={c.decimals || 0} />
                  </p>
                  <p className="mt-1.5 text-[13px] text-[color:var(--bp-text-body-on-light)]">{c.label}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <Link
              to="/markets"
              data-testid="intel-explore-markets-link"
              className="mt-10 inline-flex items-center gap-2 rounded-full border border-[color:var(--bp-teal)] px-6 py-3.5 text-sm font-semibold text-[#0A1F3D] transition-colors hover:bg-[color:var(--bp-teal)]"
            >
              Explore market intelligence
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
